import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import FormField from '../shared/FormField';
import { workflowsApi } from '../../api/facades';

const inputClass = 'w-full rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none';

const WorkflowCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const create = useMutation({
    mutationFn: () => workflowsApi.create({ name: name.trim(), description: description.trim() || null }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      const id = data?.data?.id ?? data?.data?.data?.id;
      if (id) navigate(`/dashboard/workflows/${id}`);
      else navigate('/dashboard/workflows');
      toast.success('Workflow created.');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || e?.message || 'Create failed.'),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    create.mutate();
  };

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <h1 className="text-xl font-bold text-slate-700 dark:text-slate-200 mb-4">New Workflow</h1>
      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl">
        <FormField label="Name" hint="Required">
          <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} required placeholder="e.g. Document review" />
        </FormField>
        <div className="md:col-span-2">
          <FormField label="Description">
            <textarea className={inputClass} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional description" />
          </FormField>
        </div>
        <div className="md:col-span-2 flex gap-2">
          <Button variant="secondary" type="button" onClick={() => navigate(-1)}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!name.trim() || create.isPending}>{create.isPending ? 'Creating…' : 'Create'}</Button>
        </div>
      </form>
    </div>
  );
};

export default WorkflowCreatePage;
